// utils/AlertContext.tsx

import { createContext, useState, useContext, ReactNode } from 'react';

interface AlertContextProps {
  alertMessage: string | null;
  alertType: 'error' | 'success' | 'info' | 'warning';
  showAlert: (message: string, type: 'error' | 'success' | 'info' | 'warning') => void;
  closeAlert: () => void;
}

const AlertContext = createContext<AlertContextProps | undefined>(undefined);

const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<'error' | 'success' | 'info' | 'warning'>('info');

  const showAlert = (message: string, type: 'error' | 'success' | 'info' | 'warning') => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => {
      closeAlert(); // Auto-hide the alert after 3 seconds
    }, 3000);
  };

  const closeAlert = () => {
    setAlertMessage(null);
  };

  return (
    <AlertContext.Provider value={{ alertMessage, alertType, showAlert, closeAlert }}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export default AlertProvider;
